import { formatCurrency } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';

import { ISitemateAssignedUserWithTaskUrls, User, UserTypes } from '@site-mate/dashpivot-shared-library';
import {
  CompanyPlanTypes,
  DashpivotPricingPlans,
  LicenseTypes,
  PricingRatesDefaults,
  PricingService,
  SitematePricingPlans,
} from '@site-mate/sitemate-global-shared';

import { ICompanyHomeSettings } from 'app/home/company-home-settings.model';
import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { CompanyService } from 'app/shared/service/company.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { TaskService } from 'app/shared/service/task.service';

@Component({
  selector: 'cc-company-billing-licenses',
  templateUrl: './company-billing-licenses.component.html',
  styleUrls: ['./company-billing-licenses.component.scss'],
})
export class CompanyBillingLicensesTableComponent implements OnInit, OnChanges {
  @Input() model: ICompanyHomeSettings;
  @Input() isSuperAdmin: boolean;
  @Input() pricingInputs: { [key: string]: any };

  contributorsUsedLicensesCount: number;
  visitorsUsedLicensesCount: number;
  sitematePaidUsedLicensesCount: number;

  isLoading = true;

  pricingService = new PricingService();

  readonly companyPlanTypes = CompanyPlanTypes;
  readonly dashpivotLicensePlans = [
    CompanyPlanTypes.Standard,
    CompanyPlanTypes.Pro,
    CompanyPlanTypes.Enterprise,
  ];
  readonly sitematePaidLicensePlans = [CompanyPlanTypes.Pro, CompanyPlanTypes.Enterprise];
  readonly ratesDefaults = PricingRatesDefaults;

  constructor(
    protected readonly companyService: CompanyService,
    protected readonly taskService: TaskService,
    protected readonly errorHandler: ErrorHandler,
  ) {}

  async ngOnInit() {
    try {
      const [contributors, visitors, sitemate] = await this.getCompanyUsersAndSitemateUsers();

      this.contributorsUsedLicensesCount = contributors?.filter((user) => user.isActivated).length;
      this.visitorsUsedLicensesCount = visitors?.filter((user) => user.isActivated).length;
      this.sitematePaidUsedLicensesCount = sitemate?.length;

      this.model.sitemateFreeUsersLicensesRate = PricingRatesDefaults.Free;
      this.setVisitorsLicensesRate();
      this.onDashpivotContributorsLicenseChange();
      this.onSitematePaidLicenseChange();
    } catch (error) {
      this.errorHandler.handle(error);
    }

    this.isLoading = false;
  }

  ngOnChanges(changes: SimpleChanges) {
    const { firstChange, currentValue, previousValue } = changes.pricingInputs;

    if (!firstChange && currentValue !== previousValue) {
      this.setVisitorsLicensesRate();
      this.onDashpivotContributorsLicenseChange();
      this.onSitematePaidLicenseChange();
    }
  }

  get showTable() {
    if (this.model.planType === this.companyPlanTypes.Legacy) {
      return false;
    }

    return true;
  }

  setVisitorsLicensesRate() {
    if (this.model.planType === this.companyPlanTypes.Standard) {
      this.model.visitorsLicensesRate = PricingRatesDefaults.NA;
    } else {
      this.model.visitorsLicensesRate = PricingRatesDefaults.Free;
    }
  }

  onDashpivotContributorsLicenseChange() {
    const { paymentCurrency, subscriptionTerm, planType, contributorsLicenses } = this.model;
    const hasInvalidInputs = !paymentCurrency || !subscriptionTerm || !planType;
    const isAmountValid = contributorsLicenses > 0;
    const plansNotAvailable = [CompanyPlanTypes.Legacy, CompanyPlanTypes.FreeTrial];

    if (hasInvalidInputs || !isAmountValid || plansNotAvailable.includes(planType)) {
      this.model.contributorsLicensesRate = PricingRatesDefaults.NA;
      return;
    }

    try {
      const licensePrice = this.getPricing(LicenseTypes.DashpivotContributor, contributorsLicenses);

      this.model.contributorsLicensesRate = formatCurrency(licensePrice, 'en-US', '');
    } catch (error) {
      this.errorHandler.handle({ error });
    }
  }

  onSitematePaidLicenseChange() {
    const { paymentCurrency, subscriptionTerm, planType, sitematePaidUsersLicenses } = this.model;
    const hasInvalidInputs = !paymentCurrency || !subscriptionTerm || !planType;
    const isAmountValid = sitematePaidUsersLicenses > 0;
    const plansNotAvailable = [
      CompanyPlanTypes.Legacy,
      CompanyPlanTypes.FreeTrial,
      CompanyPlanTypes.Standard,
    ];

    if (hasInvalidInputs || !isAmountValid || plansNotAvailable.includes(planType)) {
      this.model.sitematePaidUsersLicensesRate = PricingRatesDefaults.NA;
      return;
    }

    try {
      const sitematePaidPrice = this.getPricing(LicenseTypes.SitematePaidUser, sitematePaidUsersLicenses);

      this.model.sitematePaidUsersLicensesRate = formatCurrency(sitematePaidPrice, 'en-US', '');
    } catch (error) {
      this.errorHandler.handle({ error });
    }
  }

  private async getCompanyUsersAndSitemateUsers(): Promise<
    [User[], User[], ISitemateAssignedUserWithTaskUrls[]] | any[]
  > {
    return Promise.all([
      this.companyService.getUsers(this.model.id, UserTypes.Contributor).toPromise(),
      this.companyService.getUsers(this.model.id, UserTypes.Visitor).toPromise(),
      this.taskService.getAssignedUsers(AppHierarchy.Organisation, this.model.id).toPromise(),
    ])
      .then((results) => results)
      .catch((error) => {
        throw new Error(`An error occurred: ${error.message}`);
      });
  }

  private getPricing(licenseType: LicenseTypes, amountOfPaidLicenses: number): number {
    const inputs = {
      licenseType,
      amountOfPaidLicenses,
      planType: this.model.planType as DashpivotPricingPlans | SitematePricingPlans,
      subscriptionTerm: this.model.subscriptionTerm,
      currency: this.model.paymentCurrency,
    };

    return this.pricingService.getPricing(inputs);
  }
}
